"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  quote: "Dr. Ojeda, FNP's approach to facial balancing is truly transformative. I feel refreshed, not 'done'.",
  name: "Elena R.",
  role: "Executive",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/client_elena_portrait.webp"
}, {
  quote: "The concierge experience is unmatched. No waiting, no stress—just expert care in the comfort of my home.",
  name: "Marcus T.",
  role: "Entrepreneur",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/client_marcus_portrait.png"
}, {
  quote: "Starting preventative filler in my late 20s was the best decision. My skin looks smoother and more vibrant than ever.",
  name: "Sarah L.",
  role: "Creative Director",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711910/img/client_sarah_portrait.webp"
}];
export default function Transformations() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: carouselRef,
    isVisible: carouselVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(testimonials.length);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setSelected(api.selectedScrollSnap());
    const onSelect = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);
  return <section data-section-id="9826" id="effortless-transformations" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-16">
        <div ref={headerRef} className={`mx-auto max-w-[680px] text-center transition-all duration-[600ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-default font-medium text-foreground" style={{
          fontSize: "clamp(2rem, 3.8vw, 3.25rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.1
        }}>
            Refined{" "}
            <em className={`font-serif italic transition-opacity duration-700 ${headerVisible ? "opacity-100" : "opacity-0"}`} style={{
            color: "var(--primary)",
            transitionDelay: "200ms"
          }}>
              results
            </em>
          </h2>
          <p className="mt-4 font-default text-muted-foreground" style={{
          fontSize: "clamp(1.125rem, 1.3vw, 1.25rem)",
          lineHeight: 1.65,
          letterSpacing: "-0.005em"
        }}>
            Stories of confidence, symmetry, and natural beauty from our clients.
          </p>
        </div>

        <div ref={carouselRef} className={`mt-10 md:mt-12 transition-all duration-[600ms] ease-out ${carouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "120ms",
        willChange: carouselVisible ? "opacity" : "auto"
      }}>
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((t, i) => <CarouselItem data-index={i} key={i} className="pl-4 md:pl-6 basis-full md:basis-[70%] lg:basis-[55%]">
                  <article className="h-full bg-card text-card-foreground p-7 md:p-12 flex flex-col rounded-lg shadow-lg transition-opacity duration-300">
                    <blockquote className="font-serif font-normal text-card-foreground" style={{
                  fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                  lineHeight: 1.3,
                  letterSpacing: "-0.012em"
                }}>
                      {t.quote}
                    </blockquote>

                    <div className="mt-10 flex items-center gap-4">
                      <SafeHydrate><Avatar className="size-12 rounded-lg overflow-hidden">
                        <AvatarImage src={t.avatar} alt={t.name} className="object-cover" />
                        <AvatarFallback className="bg-accent text-accent-foreground font-default">
                          {t.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div className="flex flex-col">
                        <span className="font-default font-medium text-card-foreground" style={{
                      fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
                      lineHeight: 1.3,
                      letterSpacing: "-0.005em"
                    }}>
                          {t.name}
                        </span>
                        <span className="font-default text-muted-foreground" style={{
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      letterSpacing: "0.01em"
                    }}>
                          {t.role}
                        </span>
                      </div>
                    </div>
                  </article>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          <div className="mt-10 hidden md:flex items-center justify-center gap-8">
            <Button variant="ghost" size="icon" onClick={() => api?.scrollPrev()} aria-label="Previous testimonial" className="text-foreground opacity-60 hover:opacity-100 hover:bg-transparent transition-opacity duration-200">
              <ArrowLeft className="size-5" strokeWidth={1.5} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => api?.scrollNext()} aria-label="Next testimonial" className="text-foreground opacity-60 hover:opacity-100 hover:bg-transparent transition-opacity duration-200">
              <ArrowRight className="size-5" strokeWidth={1.5} />
            </Button>
          </div>

          <div className="mt-8 flex md:hidden items-center justify-center gap-2">
            {Array.from({
            length: count
          }).map((_, i) => <Button data-index={i} key={i} variant="ghost" size="sm" onClick={() => api?.scrollTo(i)} aria-label={`Go to testimonial ${i + 1}`} className="p-0 h-1.5 rounded-lg transition-all duration-300 hover:bg-transparent" style={{
            width: selected === i ? "24px" : "8px",
            backgroundColor: selected === i ? "var(--primary)" : "var(--border)"
          }} />)}
          </div>
        </div>
      </div>
    </section>;
}